import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { CheckCircle2, XCircle, FileText, Trash2, Building2 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { RoleGate } from '@/components/auth/RoleGate';
import { Label } from '@/components/ui/Label';
import { useRequest, useReviewRequest, useDeleteRequest } from '@/api/requests';
import { useRequestActivity } from '@/api/audit';
import { RequestStatusBadge } from '@/components/requests/RequestStatusBadge';
import { SubmitRequestModal } from '@/components/requests/SubmitRequestModal';
import { ActivityTimeline } from '@/components/audit/ActivityTimeline';
import { CommentsPanel } from '@/components/comments/CommentsPanel';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useIsClient } from '@/hooks/useIsClient';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';

export function RequestDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { data, isLoading, isError, error } = useRequest(id);
  const {
    data: activity,
    isLoading: activityLoading,
    isError: activityError,
    error: activityErr,
  } = useRequestActivity(id);
  const review = useReviewRequest();
  const deleteReq = useDeleteRequest();
  const navigate = useNavigate();
  const toast = useToast();
  const isClient = useIsClient();
  usePageTitle(data?.title ?? 'Request');

  const [submitting, setSubmitting] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const decide = async (outcome: 'accepted' | 'rejected') => {
    try {
      await review.mutateAsync({ id, outcome, note: reviewNote.trim() || undefined });
      toast.success(`Request ${outcome}`);
      setReviewNote('');
    } catch (e) {
      toast.error('Review failed', apiError(e));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteReq.mutateAsync(id);
      toast.success('Request deleted');
      navigate('/requests', { replace: true });
    } catch (e) {
      toast.error('Delete failed', apiError(e));
      setConfirmDelete(false);
    }
  };

  return (
    <AppShell
      crumbs={[
        { label: isClient ? 'To-do' : 'Requests', to: '/requests' },
        { label: data?.title ?? 'Request' },
      ]}
    >
      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner size={28} />
        </div>
      ) : isError || !data ? (
        <Card>
          <div className="text-sm text-red-600">{apiError(error) || 'Request not found'}</div>
        </Card>
      ) : (
        <>
        <div className="space-y-4">
          {/* Top row: request info + actions side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4 items-start">
            <Card
              title={data.title}
              actions={
                <div className="flex items-center gap-2">
                  <RequestStatusBadge status={data.status} />
                  <RoleGate roles={['sysadmin', 'manager', 'staff']}>
                    {confirmDelete ? (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-red-600 font-medium">Delete?</span>
                        <Button variant="danger" onClick={handleDelete} loading={deleteReq.isPending} className="text-xs px-2 py-1">Yes</Button>
                        <Button variant="secondary" onClick={() => setConfirmDelete(false)} className="text-xs px-2 py-1">No</Button>
                      </div>
                    ) : (
                      <Button variant="ghost" onClick={() => setConfirmDelete(true)} className="text-xs text-red-600 hover:bg-red-50 px-2 py-1">
                        <Trash2 size={13} /> Delete
                      </Button>
                    )}
                  </RoleGate>
                </div>
              }
            >
              {data.instructions && (
                <p className="text-sm text-slate-600 mb-4">{data.instructions}</p>
              )}

              <div className="text-xs text-slate-500 mb-4 flex flex-wrap gap-4">
                <span>Created {format(new Date(data.created_at), 'PP p')}</span>
                {data.due_date && <span>Due {format(new Date(data.due_date), 'PP')}</span>}
                {data.organization_name && (
                  <span className="inline-flex items-center gap-1">
                    <Building2 size={11} />
                    {data.organization_name}
                  </span>
                )}
                {(data.requestor_name || data.requestor_id) && (
                  <span>Requested by: {isClient ? 'BillerBay' : (data.requestor_name ?? data.requestor_id)}</span>
                )}
                {(data.responder_name || data.responder_id) && !isClient && (
                  <span>Submitted by: {data.responder_name ?? data.responder_id}</span>
                )}
              </div>

              {data.submitted_file_id && (
                <div className="mb-4 bg-green-50 border border-green-200 p-3">
                  <div className="text-xs text-green-700 font-semibold mb-1">Submitted file</div>
                  <Link
                    to={`/files/${data.submitted_file_id}`}
                    className="inline-flex items-center gap-2 text-sm text-bb-navy hover:underline"
                  >
                    <FileText size={14} />
                    View submitted file
                  </Link>
                </div>
              )}

              {data.review_note && (
                <div className="mt-4 bg-slate-50 p-3 text-sm">
                  <div className="text-xs text-slate-500 mb-1">Review note</div>
                  {data.review_note}
                </div>
              )}
            </Card>

            <Card title="Actions">
              {(data.status === 'open' || data.status === 'overdue') && (
                <RoleGate roles={['client_admin', 'client_user', 'staff', 'manager', 'sysadmin']}>
                  <p className="text-xs text-slate-500 mb-3">
                    Pick a file from your workspace to fulfill this request. Staff will review it and either accept or send it back.
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => setSubmitting(true)}
                    className="w-auto"
                    title="Pick a file from your workspace and attach it to this request"
                  >
                    Upload a file for this request
                  </Button>
                </RoleGate>
              )}

              {(data.status === 'submitted' || data.status === 'under_review') && (
                <>
                  <RoleGate roles={['sysadmin', 'manager', 'staff']}>
                    <p className="text-xs text-slate-500 mb-3">
                      Review the submitted file. Accepting closes the request; rejecting reopens it for the client to try again.
                    </p>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="review-note">Review note</Label>
                        <textarea
                          id="review-note"
                          rows={3}
                          value={reviewNote}
                          onChange={(e) => setReviewNote(e.target.value)}
                          className="w-full border border-bb-lilac bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bb-navy/30 focus:border-bb-navy"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          onClick={() => decide('accepted')}
                          loading={review.isPending}
                        >
                          <CheckCircle2 size={14} /> Accept
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => decide('rejected')}
                          loading={review.isPending}
                        >
                          <XCircle size={14} /> Reject
                        </Button>
                      </div>
                    </div>
                  </RoleGate>
                  <RoleGate roles={['client_admin', 'client_user']}>
                    <p className="text-xs text-slate-500">Your file has been submitted and is awaiting staff review.</p>
                  </RoleGate>
                </>
              )}

              {data.status === 'accepted' && (
                <div className="flex items-start gap-2">
                  <CheckCircle2 size={15} className="text-green-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-600">This request has been accepted. No further action is needed.</p>
                </div>
              )}

              {data.status === 'rejected' && (
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <XCircle size={15} className="text-red-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-slate-600">This submission was rejected. Please upload a corrected file.</p>
                  </div>
                  <RoleGate roles={['client_admin', 'client_user', 'staff', 'manager', 'sysadmin']}>
                    <Button variant="primary" onClick={() => setSubmitting(true)} className="w-auto">
                      Re-upload a file
                    </Button>
                  </RoleGate>
                </div>
              )}

              {data.status === 'cancelled' && (
                <div className="flex items-start gap-2">
                  <XCircle size={15} className="text-slate-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-500">This request was cancelled.</p>
                </div>
              )}
            </Card>
          </div>

          <CommentsPanel target={{ type: 'file_request', id }} />

          <Card title="Activity">
            <ActivityTimeline
              entries={activity?.items ?? []}
              isLoading={activityLoading}
              isError={activityError}
              error={activityErr}
            />
          </Card>
        </div>

        <SubmitRequestModal
          open={submitting}
          onClose={() => setSubmitting(false)}
          requestId={id}
          targetFolderId={data.target_folder_id ?? undefined}
        />
        </>
      )}
    </AppShell>
  );
}
